import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { trimText } from "../../../../../../../../utils/Functions";
import { formatCurrency } from "../../../utils/stockStatus";
import { formatDurationLabel } from "./ServiceDurationForm";

const columns = [
  { id: "index", label: "#", align: "left", width: 56, sortable: false },
  { id: "title", label: "Title", align: "left", sortable: true },
  { id: "description", label: "Description", align: "left", sortable: true },
  { id: "duration", label: "Duration", align: "left", sortable: true },
  { id: "price", label: "Price", align: "right", sortable: true },
  { id: "pdv", label: "PDV", align: "right", sortable: true },
  { id: "priceWithTax", label: "PriceWithVat", align: "right", sortable: true },
];

// Mirrors WarehouseTable.jsx's shell/header exactly. No pagination -
// services are a clinic-catalog-sized list (not warehouse-item-scale) and
// the backend has no paged endpoint for them today. Sorting is client-side
// for the same reason (the parent tab sorts the already-fetched array and
// passes order/orderBy down), unlike Items' server-side sort.
export default function WarehouseServicesTable({
  items,
  currency,
  isLoading,
  emptyMessage,
  onRowClick,
  order,
  orderBy,
  onSort,
}) {
  const { t } = useTranslation();

  function handleSort(columnId) {
    if (!onSort) return;
    const isAsc = orderBy === columnId && order === "asc";
    onSort(columnId, isAsc ? "desc" : "asc");
  }

  return (
    <MUI.Paper
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "divider",
        overflow: "hidden",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      {/* Height-capped (unlike WarehouseTable) because there's no pagination
          here: an uncapped list pushes the horizontal scrollbar below the
          fold, leaving narrow screens with no visible way to reach the
          right-hand columns. stickyHeader keeps column labels in view. */}
      <MUI.TableContainer
        sx={{
          overflow: "auto",
          minWidth: 0,
          maxHeight: "max(360px, calc(100vh - 360px))",
        }}
      >
        <MUI.Table stickyHeader sx={{ minWidth: 900 }}>
          <MUI.TableHead>
            <MUI.TableRow
              sx={{
                "& th": {
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? "grey.50"
                      : "rgba(255,255,255,0.04)",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "text.secondary",
                  borderBottom: "2px solid",
                  borderBottomColor: "divider",
                },
              }}
            >
              {columns.map((column) => (
                <MUI.TableCell
                  key={column.id}
                  align={column.align}
                  sx={{ width: column.width }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  {column.id === "index" ? (
                    ""
                  ) : column.sortable ? (
                    <MUI.TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleSort(column.id)}
                      data-testid={`warehouse.services.sort.${column.id}`}
                    >
                      {t(column.label)}
                    </MUI.TableSortLabel>
                  ) : (
                    t(column.label)
                  )}
                </MUI.TableCell>
              ))}
            </MUI.TableRow>
          </MUI.TableHead>
          <MUI.TableBody>
            {isLoading ? (
              <MUI.TableRow>
                <MUI.TableCell colSpan={7} sx={{ border: 0, py: 6 }}>
                  <MUI.Stack alignItems="center" justifyContent="center">
                    <MUI.CircularProgress size={28} />
                  </MUI.Stack>
                </MUI.TableCell>
              </MUI.TableRow>
            ) : items.length === 0 ? (
              <MUI.TableRow>
                <MUI.TableCell colSpan={7} sx={{ border: 0, py: 3 }}>
                  <MUI.Typography
                    variant="body1"
                    color={"grayText"}
                    data-testid="warehouse.services.empty-message"
                  >
                    {emptyMessage ? t(emptyMessage) : t("NoServices")}
                  </MUI.Typography>
                </MUI.TableCell>
              </MUI.TableRow>
            ) : (
              items.map((item, index) => (
                <MUI.TableRow
                  hover
                  key={item.id}
                  onClick={() => onRowClick(item)}
                  sx={{
                    cursor: "pointer",
                    "&:last-child td": { borderBottom: 0 },
                  }}
                  data-testid={`warehouse.services.row.${item.id}`}
                >
                  <MUI.TableCell sx={{ color: "text.disabled" }}>
                    {index + 1}
                  </MUI.TableCell>
                  <MUI.TableCell>
                    <MUI.Tooltip title={item.title}>
                      <MUI.Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        data-testid={`warehouse.services.row.${item.id}.title`}
                      >
                        {trimText(item.title, 30)}
                      </MUI.Typography>
                    </MUI.Tooltip>
                  </MUI.TableCell>
                  <MUI.TableCell>
                    {item.description ? (
                      <MUI.Tooltip title={item.description}>
                        <MUI.Typography
                          variant="body2"
                          color="text.secondary"
                          data-testid={`warehouse.services.row.${item.id}.description`}
                        >
                          {trimText(item.description, 30)}
                        </MUI.Typography>
                      </MUI.Tooltip>
                    ) : (
                      <MUI.Typography
                        variant="body2"
                        color="text.disabled"
                        data-testid={`warehouse.services.row.${item.id}.description`}
                      >
                        N/A
                      </MUI.Typography>
                    )}
                  </MUI.TableCell>
                  <MUI.TableCell>
                    <MUI.Typography
                      variant="body2"
                      color="text.secondary"
                      data-testid={`warehouse.services.row.${item.id}.duration`}
                    >
                      {item.duration ? formatDurationLabel(item.duration) : "N/A"}
                    </MUI.Typography>
                  </MUI.TableCell>
                  <MUI.TableCell align="right">
                    <MUI.Typography
                      variant="body2"
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                      data-testid={`warehouse.services.row.${item.id}.price`}
                    >
                      {item.price != null ? formatCurrency(item.price, currency) : "—"}
                    </MUI.Typography>
                  </MUI.TableCell>
                  <MUI.TableCell align="right">
                    <MUI.Typography
                      variant="body2"
                      color="text.secondary"
                      data-testid={`warehouse.services.row.${item.id}.pdv`}
                    >
                      {item.pdv != null ? `${Number(item.pdv)}%` : "—"}
                    </MUI.Typography>
                  </MUI.TableCell>
                  <MUI.TableCell align="right">
                    <MUI.Typography
                      variant="body2"
                      sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
                      data-testid={`warehouse.services.row.${item.id}.price-with-vat`}
                    >
                      {item.priceWithTax != null ? formatCurrency(item.priceWithTax, currency) : "—"}
                    </MUI.Typography>
                  </MUI.TableCell>
                </MUI.TableRow>
              ))
            )}
          </MUI.TableBody>
        </MUI.Table>
      </MUI.TableContainer>
    </MUI.Paper>
  );
}
